import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { toast } from "react-toastify";
import api from "../../api";
import { TYPE_LABELS, moneyAuto } from "./shared";
import { describeStyle } from "./styleOptions";

const STATUS_CHIPS = {
  scheduled: { label: "Scheduled", color: "info" },
  running: { label: "Starting", color: "warning" },
  done: { label: "Queued", color: "success" },
  cancelled: { label: "Cancelled", color: "default" },
  failed: { label: "Failed", color: "error" },
};

const MODE_LABELS = {
  missing: "Missing videos",
  failed: "Retry failed / held",
  questions: "Chosen questions",
  restyle: "Re-style finished",
};

function describeRun(run, setNames) {
  if (run.label) return `${run.label} (${run.sets} set${run.sets === 1 ? "" : "s"})`;
  return run.items
    .map((it) => {
      const name = it.name || setNames[`${it.type}-${it.id}`] || `${TYPE_LABELS[it.type]} #${it.id}`;
      return it.question_ids ? `${name} (${it.question_ids.length} q)` : name;
    })
    .join(", ");
}

/**
 * Upcoming and recent scheduled runs. schedules comes from the page, which reloads it.
 */
export default function SchedulesPanel({ schedules, setNames, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const upcoming = schedules.filter((s) => s.status === "scheduled" || s.status === "running");

  if (schedules.length === 0) return null;

  const cancel = async (s) => {
    setBusyId(s.id);
    try {
      const res = await api.delete(`/admin/explanation-videos/schedules/${s.id}`);
      toast.success(res.data?.message || "Cancelled.");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not cancel the run.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1, cursor: "pointer" }}
        onClick={() => setOpen((o) => !o)}
      >
        <ScheduleIcon fontSize="small" color="action" />
        <Typography variant="body2" fontWeight={600}>
          Scheduled runs
        </Typography>
        {upcoming.length > 0 && <Chip size="small" color="info" label={`${upcoming.length} upcoming`} />}
        {upcoming[0] && (
          <Typography variant="caption" color="text.secondary">
            next {new Date(upcoming[0].run_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={180}>Run at</TableCell>
              <TableCell>What</TableCell>
              <TableCell width={170}>Mode</TableCell>
              <TableCell width={80}>Cost</TableCell>
              <TableCell width={120}>Status</TableCell>
              <TableCell width={100} />
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.map((s) => {
              const chip = STATUS_CHIPS[s.status] || { label: s.status, color: "default" };
              return (
                <TableRow key={s.id}>
                  <TableCell>
                    <Typography variant="body2">{new Date(s.run_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
                    {s.created_by && (
                      <Typography variant="caption" color="text.secondary">
                        by {s.created_by}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{describeRun(s, setNames)}</Typography>
                    {s.style && (
                      <Typography variant="caption" color="primary" display="block">
                        Style: {describeStyle(s.style)}
                      </Typography>
                    )}
                    {s.note && (
                      <Typography variant="caption" color="error">
                        {s.note}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{MODE_LABELS[s.mode] || s.mode}</TableCell>
                  <TableCell>{moneyAuto(s.estimated_cost_usd)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={chip.color}
                      label={s.status === "done" && s.dispatched !== null ? `${chip.label} ${s.dispatched}` : chip.label}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {s.status === "scheduled" && (
                      <Button size="small" color="error" disabled={busyId === s.id} onClick={() => cancel(s)}>
                        Cancel
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Collapse>
    </Paper>
  );
}
